import { Component, Input, OnChanges } from '@angular/core';
import Highcharts from 'highcharts';
import { ColorPalette, PageSection } from '../../../models/end-client-reporting-package';
import { ChartSeries, ReportBlock, chartColors } from '../report-aggregation';
import { SLICE_CENTRE, SLICE_SIZE_PX, SLICE_SPACING, sliceLegend, slicePoints, withDeckMotion } from './chart-layout';

@Component({
  selector: 'app-donut-chart-block',
  standalone: false,
  template: `
    @if (chartOptions) {
      <highcharts-chart [Highcharts]="highcharts" [oneToOne]="true" [options]="chartOptions"
                        style="width: 100%; display: block;"></highcharts-chart>
    } @else if (donuts.length) {
      <div class="donut-grid">
        @for (donut of donuts; track donut.label) {
          <div class="donut-cell">
            <highcharts-chart [Highcharts]="highcharts" [oneToOne]="true" [options]="donut.options"
                              style="width: 100%; display: block;"></highcharts-chart>
            <p class="grey5 nav-font12 text-center mb-0">{{ donut.label }}</p>
          </div>
        }
      </div>
    }
  `,
  styles: [`
    .donut-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
      gap: 0.5rem;
    }
    /* Groups the ring with its label; the card behind the whole block supplies the white. It used
       to paint its own, which now would be white on white. */
    .donut-cell {
      padding-bottom: 0.5rem;
    }
    .donut-grid p {
      line-height: 1.2;
      word-break: break-word;
    }
  `]
})
export class DonutChartBlockComponent implements ReportBlock, OnChanges {
  @Input({ required: true }) block!: PageSection;
  @Input({ required: true }) series!: ChartSeries[];
  @Input({ required: true }) palette!: ColorPalette;
  @Input() alignCount?: number;
  @Input() compact?: boolean;

  highcharts = Highcharts;
  chartOptions: Highcharts.Options | null = null;
  donuts: { label: string; options: Highcharts.Options }[] = [];

  ngOnChanges() {
    const series = this.series[0];
    const points = series?.points ?? [];

    // A multi-punch question's options sum past 100%, so one ring either contradicts its own segment
    // sizes or quietly switches to "share of all selections" -- a number every reader hears as
    // "share of people". Per option, each donut sums to 100% and still means % of respondents, so one
    // metric holds across the whole deck. Moved here from the pie block, where "Pie" drawing donuts
    // was a surprise.
    if (series?.multiResponse) {
      this.chartOptions = null;
      this.donuts = points.map(point => ({
        label: point.label,
        options: this.optionDonut(point.label, point.percent, point.value),
      }));
      return;
    }

    this.donuts = [];

    const legendRows = Math.max(points.length, this.alignCount ?? 0);
    // One per slice: a nine-option question used to give options 6-9 the colours of options 1-4.
    const roles = chartColors(this.palette, Math.max(1, points.length));
    const hideLabels = !!(this.block.reportExtraData as { hideLabels?: boolean }).hideLabels;

    this.chartOptions = withDeckMotion({
      chart: {
        type: 'pie',
        // Sized by the legend, which is the tall side now: the ring is a fixed 110px beside it.
        height: Math.max(150, legendRows * 18 + 40),
        spacing: SLICE_SPACING,
        style: { fontFamily: 'inherit' },
      },
      title: { text: undefined },
      credits: { enabled: false },
      tooltip: { pointFormat: '<b>{point.y:.0f}%</b> ({point.custom.count})' },
      // A pie's answer text lives in its legend, so that is what the setting hides here.
      legend: hideLabels ? { enabled: false } : sliceLegend(!!this.compact),
      plotOptions: {
        pie: {
          // The hole is deliberately empty -- the donut is a stylistic alternative to the pie, not a
          // carrier for an extra figure.
          //
          // A flat 110px, not a share of the chart: the ring is a glyph beside the key rather than
          // the thing being read, and a percentage size would have it swell or shrink with the
          // number of answers. The prototype's is 92 with a 50px hole, which is this ratio.
          innerSize: '54%',
          size: SLICE_SIZE_PX,
          center: SLICE_CENTRE,
          showInLegend: true,
          borderWidth: 0,
          // Square slices: Highcharts rounds every slice by default, which puts a curve at each
          // join between two answers rather than only where the ring begins and ends.
          borderRadius: 0,
          // Off: every figure is in the legend, and a thin slice cannot hold its own label anyway
          // -- the 1% answers in a seven-answer question are most of why this module exists.
          dataLabels: { enabled: false },
        },
      },
      series: [{
        type: 'pie',
        name: series?.name ?? '',
        data: slicePoints(points, roles),
      }],
    });
  }

  /** One option's take-up as a percentage of respondents, with the figure in the middle. */
  private optionDonut(label: string, percent: number, count: number): Highcharts.Options {
    return withDeckMotion({
      chart: { type: 'pie', height: 130, spacing: [4, 4, 4, 4], style: { fontFamily: 'inherit' } },
      title: {
        text: `${Math.round(percent)}%`,
        verticalAlign: 'middle',
        floating: true,
        style: { fontSize: '15px', fontWeight: '600' },
      },
      credits: { enabled: false },
      tooltip: { pointFormat: '<b>{point.y:.0f}%</b> ({point.custom.count})' },
      plotOptions: {
        pie: {
          innerSize: '65%',
          size: '85%',
          borderWidth: 0,
          // Square slices: Highcharts rounds every slice by default, which puts a curve at each
          // join between two answers rather than only where the ring begins and ends.
          borderRadius: 0,
          dataLabels: { enabled: false },
          states: { hover: { halo: { size: 0 } } },
        },
      },
      series: [{
        type: 'pie',
        name: label,
        data: [
          { name: 'Picked', y: percent, custom: { count }, colorIndex: 0 },
          {
            name: 'Did not pick',
            y: Math.max(0, 100 - percent),
            custom: { count: 0 },
            colorIndex: 0,
            className: 'chart-track',
          },
        ],
      }],
    });
  }
}
